import type { ActionPill } from "./chat"
import { DayObject, Timestamp } from "./date"
import type { TimeFrame, TripRef } from "./trip"
import { PublicUser } from "./user"

export type AgentSessionStatus =
  | "ready"
  | "pending"
  | "working"
  | "waiting"
  | "error"
  | "stopped"

export type ToolName =
  | "ask_clarifying_questions"
  | "budget_optimizer"
  | "calculator"
  | "calendar_info"
  | "compact_entity_notes"
  | "complete_working_mode"
  | "entity_id_resolver"
  | "entity_notes"
  | "escalate_to_parent"
  | "flight_fetch_more_options"
  | "flight_multi_city_search"
  | "flight_one_way_search"
  | "flight_price_statistics"
  | "flight_result_notes"
  | "flight_round_trip_search"
  | "generate_report"
  | "hotel_batch_price"
  | "hotel_price_statistics"
  | "hotel_room_availability_search"
  | "launch_flight_sub_agent"
  | "launch_hotel_search_agent"
  | "launch_research_question_agent"
  | "open_api_spec"
  | "photo_query"
  | "plan_management"
  | "read_openapi_spec"
  | "request_clarifying_questions"
  | "request_help"
  | "session_title_management"
  | "sql_execute"
  | "sql_query"
  | "sql_schema"
  | "structured_plan_management"
  | "structured_todo_management"
  | "submit_plan"
  | "sub_agent_submit_plan"
  | "sub_agent_work_completed"
  | "think"
  | "todo_management"
  | "update_entity_notes"
  | "update_flight_result_notes"
  | "update_status"
  | "work_completed"

// Extracted from backend Llm model names
export type ModelName =
  | "claude45_sonnet_200k"
  | "claude45_sonnet_16k"
  | "claude45_haiku"
  | "claude45_haiku_200k"
  | "gpt4o"
  | "gpt4o_mini"
  | "gpt4_1"
  | "gpt4_1_mini"

// Extracted from backend flight/hotel providers
export type ProviderName =
  | "amadeus"
  | "google_flights"
  | "hotelbeds"
  | "kayak"
  | "priceline"

export type ThinkingLevel = "default" | "off" | "low" | "medium" | "high"

export const CHAT_EVAL_AGENT_TYPE = "Chat"

// reachable only from the organization dashboard's agent tab, never the playground picker
export const ORGANIZATION_AGENT_TYPE = "Agent::OrganizationAgent"

export interface AgentTypeInfo {
  name: string
  label: string
  description?: string
  type: "full_agent" | "sub_agent"
  external_visible?: boolean
  allowed_model_ids?: string[]
  default_model_id?: string
  default_thinking_level?: Exclude<ThinkingLevel, "default"> | null
  default_thinking_level_by_model_id?: Record<
    string,
    Exclude<ThinkingLevel, "default">
  >
  session_config_descriptors?: SessionConfigDescriptor[]
}

export interface PlaygroundPermissions {
  can_view_cost: boolean
  can_use_evals: boolean
  can_use_advanced_options: boolean
  can_view_debug_panels: boolean
  can_toggle_share: boolean
  can_view_all_agents: boolean
}

export interface AgentTypesResponse {
  agent_types: AgentTypeInfo[]
  permissions: PlaygroundPermissions
}

export interface AgentModelOption {
  id: string
  provider: string
  model: string
  supports_streaming: boolean
  supports_reasoning: boolean
  supports_tools: boolean
  default_thinking_level?: Exclude<ThinkingLevel, "default"> | null
  supports_thinking_off?: boolean
  off_maps_to_level?: Exclude<ThinkingLevel, "default" | "off"> | null
  max_context: number
  max_output: number
  deprecated: boolean
  recommended?: boolean
}

export interface SessionConfigDescriptor {
  key: string
  label: string
  description: string
  value_type: "boolean" | "select" | "number"
  default_value: boolean | string | number | null
  options?: string[]
}

export interface ConversationItem {
  id: string
  role: string
  content: string | Array<unknown>
  timestamp: string
  position: number
}

export interface AgentSessionSummary {
  id: number
  uuid: string
  status: AgentSessionStatus
  default_model_name: string
  agent_type: string
  agent_class_type?: "full_agent" | "sub_agent"
  created_at: Timestamp
  updated_at: Timestamp
  title: string | null
  message_count: number
  tool_call_count: number
  max_iterations: number | null
  total_cost?: number
}

export interface AgentSessionMetrics {
  total_input_tokens: number
  total_output_tokens: number
  message_count: number
  tool_call_count: number
  total_cache_read_tokens: number
  total_cache_write_tokens: number
  total_cost: number
}

export interface ModelCostMetrics {
  total_cost: number
  call_count: number
}

export interface ModelTokenMetrics {
  call_count: number
  input: number
  output: number
  cache_read: number
  cache_write: number
  avg_duration_ms: number
}

export interface ToolMetrics {
  call_count: number
  success_count: number
  failure_count: number
  total_execution_time_ms: number
  avg_execution_time_ms: number
  total_nested_llm_cost?: number
}

export interface CacheEfficiencyMetrics {
  total_cache_read_toks: number
  total_cache_write_toks: number
  total_input_toks: number
  cache_hit_rate: number
  tokens_saved: number
  estimated_cost_saved?: number
}

export interface ProviderMetrics {
  total_calls: number
  successful_calls: number
  failed_calls: number
  total_duration_ms: number
  avg_duration_ms: number
  endpoints_used: Record<string, number>
}

export interface TimingMetrics {
  total_working_time_ms: number
  total_working_time_seconds: number
  created_at: Timestamp
  updated_at: Timestamp
}

export interface EnhancedAgentSessionMetrics {
  title: string | null
  current_mode: string
  status: AgentSessionStatus
  agent_type: string
  agent_class_type?: "full_agent" | "sub_agent" | "eval_agent"
  message_count: number
  tool_call_count: number
  working_time_ms?: number
  costs?: {
    total_cost: number
    by_model: Record<ModelName, ModelCostMetrics>
  }
  tokens?: {
    by_model: Record<ModelName, ModelTokenMetrics>
    total: {
      input: number
      output: number
      cache_read: number
      cache_write: number
    }
    /** Input tokens of this session's latest LLM call (current context size) */
    context: number
  }
  cache_efficiency?: CacheEfficiencyMetrics
  tools?: Record<ToolName, ToolMetrics>
  providers?: Record<ProviderName, ProviderMetrics>
  todos: {
    completed: number
    total: number
  }
  created_at: Timestamp
  updated_at: Timestamp
  recent_events: AgentSessionEventDetail[]
}

export interface NestedLlmCall {
  model_used: string
  purpose: string
  input_toks: number
  output_toks: number
  cache_read_toks: number
  cache_write_toks: number
  cost_estimate: number
  duration_ms: number
}

// API call tracking (for tools that call external APIs)
export interface ApiCall {
  provider: string
  endpoint: string
  duration_ms: number
  success: boolean
  error_message?: string
}

export interface EventStats {
  tokens_used?: number
  cost_estimate?: number
  input_toks?: number
  output_toks?: number
  cache_read_toks?: number
  cache_write_toks?: number
  execution_time_ms?: number
  started_at?: number
  ended_at?: number
  llm_call_duration_ms?: number
  model_used?: string
  nested_llm_calls?: NestedLlmCall[]
  api_calls?: ApiCall[]
}

export interface HotelSearchMetadata {
  search_id: string
  search_groups: string[]
  locations: string[]
  hotel_count: number
}

export interface SubAgentToolResultMetrics {
  session_id: number
  total_working_time_ms: number
  total_cost: number
  by_model: Record<
    string,
    {
      call_count: number
      total_input_toks: number
      total_output_toks: number
      total_cost: number
    }
  >
  by_tool: Record<
    string,
    {
      call_count: number
      success_count: number
      failure_count: number
      total_execution_time_ms: number
    }
  >
  by_provider?: Record<
    string,
    {
      total_calls: number
      successful_calls: number
      failed_calls: number
      total_duration_ms: number
    }
  >
}

export interface ToolResultMetadata {
  tool_name: ToolName
  success: boolean
  call_id?: string
  tool_use_id?: string
  tool_round?: number
  bot_result_type?: string
  has_entity_map?: boolean
  entity_map?: Record<string, string>
  entity_type?: string
  run_tools_again?: boolean
  artifact_key?: string
  summary?: string
  sub_agent_session_id?: number
  /** A fan-out launch (ask_websites): one child session per request, in request order. */
  sub_agent_session_ids?: number[]
  original_query?: string
  in_progress?: boolean
  pending_questions?: boolean
  questions?: Question[]
  error_code?: string
  hotel_searches?: HotelSearchMetadata
  sub_agent_metrics?: SubAgentToolResultMetrics
  browser_snapshot_number?: number
}

export interface AgentSubAgentMetrics {
  title: string | null
  current_mode: string
  status: AgentSessionStatus
  agent_type: string
  message_count: number
  tool_call_count: number
  todos: {
    completed: number
    total: number
  }
  created_at: Timestamp
  updated_at: Timestamp
  recent_events: AgentSessionEventDetail[]
  working_time_ms?: number
  costs?: {
    total_cost: number
    by_model: Record<ModelName, ModelCostMetrics>
  }
  tokens?: {
    by_model: Record<ModelName, ModelTokenMetrics>
    total: {
      input: number
      output: number
      cache_read: number
      cache_write: number
    }
    /** Input tokens of this session's latest LLM call (current context size) */
    context: number
  }
  cache_efficiency?: CacheEfficiencyMetrics
  tools?: Record<ToolName, ToolMetrics>
  providers?: Record<ProviderName, ProviderMetrics>
}

export interface AgentSessionDetail {
  id: number
  uuid: string
  status: AgentSessionStatus
  // state of the background job meant to run a pending top-level session
  pending_runner_job_state?: string | null
  agent_type: string
  agent_class_type?: "full_agent" | "sub_agent"
  default_model_name: string
  stored_model_id?: string
  effective_model_id?: string
  is_legacy_model?: boolean
  legacy_model_reason?: "disallowed_for_agent" | "unknown_model" | null
  created_at: Timestamp
  updated_at: Timestamp
  title: string | null
  message_count: number
  tool_call_count: number
  metadata: Record<string, unknown> | null
  user_id: number
  max_iterations: number | null
  total_cost: number
  current_mode?: string
  pending_plan?: AgentPlanEventDetail
  shared: boolean
  is_owner?: boolean
  owner?: PublicUser
  owner_name?: string
  owner_email?: string
  flight_provider_keys?: string[]
  registered_tools?: string[] | null
  thinking_level?: ThinkingLevel | null
  effective_thinking_level?: Exclude<ThinkingLevel, "default"> | null
  agent_detail?: AgentDetailPayload | null
}

export interface AgentDetailPayload {
  kind: string
  version: number
  data: Record<string, unknown>
}

export interface TriagePayload {
  trip_ref?: TripRef | null
  summary?: string | null
  findings: TriageFindingDetail[]
}

export interface TriageFindingDetail {
  itinerary_event_id?: number
  related_event_ids?: number[]
  conflict_kind: string
  severity: "high" | "medium" | "low"
  strategy: string
  actor: string
  headline: string
  deciding_facts: string
  facts?: { label: string; value: string }[]
  why: string
  proposed_resolution?: string
  prompt?: string
  /** The buttons the card derives from this finding, resolved server-side. */
  actions: ActionPill[]
}

export interface PagingMeta {
  current_page: number
  next_page: number | null
  prev_page: number | null
  total_pages: number
  total_count: number
  per_page: number
}

export interface AgentSessionsResponse {
  sessions: AgentSessionSummary[]
  paging_meta: PagingMeta
}

export interface AgentSessionDetailResponse {
  session: AgentSessionDetail
}

export interface CreateSessionPayload {
  agent_type: string
  initial_message: string
  max_iterations?: number
  flight_provider_keys?: string[]
  thinking_level?: ThinkingLevel | null
  model_id?: string
  session_config?: EvaluationSessionConfig
}

export interface CreateSessionResponse {
  session: AgentSessionDetail
  status: AgentSessionStatus
  job_id?: number
  initial_events?: AgentSessionEventDetail[]
}

export interface SendMessagePayload {
  message: string
}

export interface SendMessageResponse {
  session: AgentSessionDetail
  status: AgentSessionStatus
  job_id?: number
}

export interface TextBlock {
  type: "text"
  text: string
}

export interface ToolUseBlock {
  type: "tool_use"
  id: string
  name: string
  input: Record<string, unknown>
}

export type MessageContentBlock = TextBlock | ToolUseBlock

export interface ThinkingBlock {
  type: "thinking" | "redacted_thinking"
  thinking?: string
  signature?: string
  data?: string
}

export interface AgentMessageEventDetail {
  id: number
  type: "AgentMessageEvent"
  role: "user" | "assistant" | "system"
  content: string | MessageContentBlock[]
  metadata?: {
    thinking_blocks?: ThinkingBlock[]
    [key: string]: unknown
  }
  /** The model call behind an assistant turn; absent when code wrote it. */
  stats?: EventStats
  /** Set when code, not the model, wrote the event (a fast path's injected round). */
  injected_by?: string
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentToolCallResultEventDetail {
  id: number
  type: "AgentToolCallResultEvent"
  role: "tool"
  content: string | Record<string, unknown>
  metadata: ToolResultMetadata
  stats?: EventStats
  error?: string | null
  injected_by?: string | null
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentSystemPromptEventDetail {
  id: number
  type: "AgentSystemPromptEvent"
  role: "system"
  content: string
  created_at: Timestamp
  updated_at: Timestamp
}

type StructuredTodo = {
  type: "structured"
  name: string
  completed: boolean
}

type SimpleTodo = {
  type: "simple"
  content: string
  complete: boolean
}

export interface AgentTodoEventDetail {
  id: number
  type: "AgentTodoEvent"
  role: "system" | "assistant"
  content: Array<StructuredTodo | SimpleTodo>
  metadata: {
    action: string
    todo_id?: number
    todo_type?: string
  }
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentModeEventDetail {
  id: number
  type: "AgentModeEvent"
  role: "system"
  mode: string
  context?: string
  transition_timestamp?: string
  created_at: Timestamp
  updated_at: Timestamp
  metadata: Record<string, unknown>
}

export interface AgentPlanEventDetail {
  id: number
  type: "AgentPlanEvent"
  role: "system" | "user" | "assistant"
  content: string | Record<string, unknown>
  status: "pending" | "approved" | "rejected" | "completed"
  metadata: {
    approved_at?: number
    rejected_at?: number
    completed_at?: number
    rejection_reason?: string
    plan_type?: string
  }
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentSubAgentResponseEventDetail {
  id: number
  type: "AgentSubAgentResponseEvent"
  role: "system"
  content: string
  metadata: Record<string, unknown>
  created_at: Timestamp
  updated_at: Timestamp
}

export type QuestionInputType =
  | "single_select"
  | "multi_select"
  | "range"
  | "dual_range"
  | "dropdown"
  | "text"
  | "pills"
  | "number"
  | "toggle"
  | "multi_field"
  | "form"
  | "otp"

export type MultiFieldType = "number" | "boolean" | "dropdown"

export interface MultiFieldDefinition {
  id: string
  label: string
  type: MultiFieldType
  options?: string[] // Required for dropdown type
  default?: string | number | boolean // Default value for this field
}

// Text field within a form row; ids are unique across the whole form
export interface FormFieldDefinition {
  id: string
  label: string
  placeholder?: string
  default?: string // Prefill value the user can edit
  optional?: boolean // When true, the user may leave this field blank
}

export interface FormRowDefinition {
  fields: FormFieldDefinition[] // Rendered side by side within the row
}

export interface QuestionConfig {
  min?: number
  max?: number
  step?: number
  unit?: string
  default?: number | string | boolean
  defaultMin?: number // For dual_range: default minimum value
  defaultMax?: number // For dual_range: default maximum value
  placeholder?: string
  description?: string // For form: guidance line rendered under the question title
  fields?: MultiFieldDefinition[] // Required for multi_field type
  rows?: FormRowDefinition[] // Required for form type
  length?: number // Required for otp type: number of digits
}

export interface QuestionOptionGroup {
  label: string
  options: string[]
}

export interface Question {
  id: string
  title: string
  question_text?: string // Optional for multi_field (each field has its own label)
  input_type: QuestionInputType
  options: string[]
  option_groups?: QuestionOptionGroup[] // single_select only: options grouped under labeled headings
  config?: QuestionConfig
  mandatory?: boolean
}

// A grouped option submits the same flat value the backend derives into
// `options`, so answers are identical whether or not the UI grouped them.
export function groupedOptionValue(groupLabel: string, option: string): string {
  return `${groupLabel} — ${option}`
}

// Answer types - each question type has its own properly typed answer
export type DualRangeAnswer = { min: number; max: number }
export type MultiFieldAnswer = Record<string, string | number | boolean>
export type FormAnswer = Record<string, string>

// Union of all answer types
export type AnswerValue =
  | string // single_select, dropdown, text
  | string[] // multi_select, pills
  | number // range, number
  | boolean // toggle
  | DualRangeAnswer // dual_range
  | MultiFieldAnswer // multi_field
  | FormAnswer // form

export type Answers = Record<string, AnswerValue>

// Map input types to answer types for type-safe access
export type AnswerTypeMap = {
  single_select: string
  multi_select: string[]
  range: number
  dual_range: DualRangeAnswer
  dropdown: string
  text: string
  pills: string[]
  number: number
  toggle: boolean
  multi_field: MultiFieldAnswer
  form: FormAnswer
  otp: string
}

// Type guards for answer types
export function isDualRangeAnswer(
  value: AnswerValue
): value is DualRangeAnswer {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "min" in value &&
    "max" in value &&
    typeof (value as DualRangeAnswer).min === "number" &&
    typeof (value as DualRangeAnswer).max === "number"
  )
}

export function isMultiFieldAnswer(
  value: AnswerValue
): value is MultiFieldAnswer {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !isDualRangeAnswer(value)
  )
}

export function isFormAnswer(value: AnswerValue): value is FormAnswer {
  return (
    isMultiFieldAnswer(value) &&
    Object.values(value).every((v) => typeof v === "string")
  )
}

// Helper to get typed answer for a question
export function getTypedAnswer<T extends QuestionInputType>(
  answers: Answers,
  questionId: string,
  _inputType: T
): AnswerTypeMap[T] | undefined {
  return answers[questionId] as AnswerTypeMap[T] | undefined
}

export interface AgentQuestionEventDetail {
  id: number
  type: "AgentQuestionEvent"
  role: "assistant"
  content: {
    questions: Question[]
  }
  status: "pending" | "answered" | "skipped"
  answers: Answers
  metadata: Record<string, unknown>
  agent_session_id: number
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentHelpRequestEventDetail {
  id: number
  type: "AgentHelpRequestEvent"
  role: "assistant"
  content: string
  reason: string | null
  metadata: Record<string, unknown>
  agent_session_id: number
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentReportEventDetail {
  id: number
  type: "AgentReportEvent"
  role: "assistant"
  report_name: string
  sql_query: string
  params: Record<string, string[]>
  created_at: Timestamp
  updated_at: Timestamp
}

export type AgentSessionEventDetail =
  | AgentMessageEventDetail
  | AgentToolCallResultEventDetail
  | AgentSystemPromptEventDetail
  | AgentTodoEventDetail
  | AgentModeEventDetail
  | AgentPlanEventDetail
  | AgentSubAgentResponseEventDetail
  | AgentQuestionEventDetail
  | AgentHelpRequestEventDetail
  | AgentReportEventDetail

export interface AgentSessionEventsResponse {
  agent_session_events: AgentSessionEventDetail[]
}

export interface SessionFormData {
  selectedAgentType: string | null
  selectedModelId: string | null
  availableModels: AgentModelOption[]
  maxIterations: number
  message: string
  thinkingLevel: ThinkingLevel
  shared: boolean
  sessionConfigOverrides: Record<string, boolean | string>
}

// Type guards for proper type narrowing
export function isAgentMessageEvent(
  event: AgentSessionEventDetail
): event is AgentMessageEventDetail {
  return event.type === "AgentMessageEvent"
}

export function isAgentToolCallResultEvent(
  event: AgentSessionEventDetail
): event is AgentToolCallResultEventDetail {
  return event.type === "AgentToolCallResultEvent"
}

export function isAgentSystemPromptEvent(
  event: AgentSessionEventDetail
): event is AgentSystemPromptEventDetail {
  return event.type === "AgentSystemPromptEvent"
}

export function isAgentTodoEvent(
  event: AgentSessionEventDetail
): event is AgentTodoEventDetail {
  return event.type === "AgentTodoEvent"
}

export function isAgentModeEvent(
  event: AgentSessionEventDetail
): event is AgentModeEventDetail {
  return event.type === "AgentModeEvent"
}

export function isAgentPlanEvent(
  event: AgentSessionEventDetail
): event is AgentPlanEventDetail {
  return event.type === "AgentPlanEvent"
}

export function isAgentSubAgentResponseEvent(
  event: AgentSessionEventDetail
): event is AgentSubAgentResponseEventDetail {
  return event.type === "AgentSubAgentResponseEvent"
}

export function isAgentQuestionEvent(
  event: AgentSessionEventDetail
): event is AgentQuestionEventDetail {
  return event.type === "AgentQuestionEvent"
}

export function isAgentHelpRequestEvent(
  event: AgentSessionEventDetail
): event is AgentHelpRequestEventDetail {
  return event.type === "AgentHelpRequestEvent"
}

export function isAgentReportEvent(
  event: AgentSessionEventDetail
): event is AgentReportEventDetail {
  return event.type === "AgentReportEvent"
}

// --- Agent Evaluation Types ---

export type TimeTargetGrade = "pass" | "over" | "fail" | "severe_fail"

export interface TimeTargetResult {
  passed: boolean
  target_seconds: number
  actual_seconds: number
  ratio: number
  grade: TimeTargetGrade
}

export type EvaluationRunStatus =
  | "pending"
  | "running"
  | "grading"
  | "passed"
  | "failed"
  | "error"
  | "timeout"

export type AssertionSeverity = "critical" | "high" | "normal" | "minor"

export interface EvaluationSessionConfig {
  thinking_level?: ThinkingLevel
  model_id?: string
  [key: string]: unknown
}

export interface EvaluationMemory {
  path: string
  key: string
  value: string
}

export type EvaluationTripStopType =
  | "Departure"
  | "Transit"
  | "Stay"
  | "Return"
  | "Visit"
  | "Eat"

export interface EvaluationTripStop {
  entity_ref: string
  days?: number
  stop_type?: EvaluationTripStopType
}

export interface EvaluationTripTimeFrame {
  start_date?: string
  duration?: number
  months?: number[]
}

export interface EvaluationTripTravelers {
  adult_count: number
  child_count: number
  infant_count: number
  pet_count: number
}

export interface EvaluationTripConfig {
  major_stops: EvaluationTripStop[]
  time_frame?: EvaluationTripTimeFrame
  travelers?: EvaluationTripTravelers
  budget?: 1 | 2 | 3 | 4
}

export interface EvaluationChatConfig {
  bot?: string | null
  vertical?: string | null
  orgid?: string | null
  trip?: EvaluationTripConfig | null
}

export type EvaluationLaunchTool = "transportation_search" | "hotel_search"

export interface EvaluationDriverConfig {
  max_turns?: number | null
  driver_objective?: string | null
  session_config?: EvaluationSessionConfig | null
  chat_config?: EvaluationChatConfig | null
  key_facts?: string[] | null
  memories?: EvaluationMemory[] | null
  launch_tool?: EvaluationLaunchTool | null
}

export interface EvaluationAssertion {
  assertion: string
  severity: AssertionSeverity
}

export interface EvaluationAssertions {
  time_target_seconds?: number | null
  items: EvaluationAssertion[]
}

export interface AssertionResult {
  assertion: EvaluationAssertion
  passed: boolean
  reason: string
  evidence?: string
}

export type DriverActionType =
  | "approve_plan"
  | "reject_plan"
  | "answer_questions"
  | "respond_to_help"
  | "send_message"
  | "complete"
  | "error"

export interface DriverActionDetails {
  rejection_reason?: string
  answers?: Record<string, string[]>
  response?: string
  message?: string
}

export interface DriverAction {
  turn: number
  timestamp: Timestamp
  action: DriverActionType
  reason: string
  details: DriverActionDetails
}

export interface AgentEvaluationSummary {
  id: number
  name: string
  description: string | null
  agent_type: string
  assertion_count: number
  total_runs: number
  avg_score: number
  total_runs_30d: number
  avg_score_30d: number
  avg_time_ms: number | null
  avg_time_ms_30d: number | null
  latest_status: EvaluationRunStatus | null
  latest_run_at: Timestamp | null
  latest_run_id?: number
  tags: string[]
  created_at: Timestamp
  updated_at: Timestamp
}

export interface EvaluationUserLookup {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  handle: string | null
}

export interface EvaluationOrganizationOption {
  orgid: string
  name: string
}

export interface AgentEvaluationDetail {
  id: number
  name: string
  description: string | null
  agent_type: string
  initial_prompt: string
  max_iterations: number
  passing_grade_threshold: number | null
  grader_model: string | null
  driver_config: EvaluationDriverConfig
  assertions: EvaluationAssertions
  preprocess_instruction?: string
  follow_up_instruction?: string
  session_user_id?: number | null
  session_user?: EvaluationUserLookup | null
  total_runs: number
  avg_score: number
  total_runs_30d: number
  avg_score_30d: number
  avg_time_ms: number | null
  avg_time_ms_30d: number | null
  latest_status: EvaluationRunStatus | null
  latest_run_at: Timestamp | null
  latest_run_id?: number
  available_models: string[]
  tags: string[]
  created_at: Timestamp
  updated_at: Timestamp
}

export interface EvaluationSessionMetric {
  id: number
  cost: number
  working_time_ms: number
  total_tokens: number
}

export interface EvaluationCostBreakdown {
  target_session: EvaluationSessionMetric | null
  driver_session: EvaluationSessionMetric | null
  grader_session: EvaluationSessionMetric | null
  total_cost: number
  total_tokens: number
  total_working_time_ms: number
}

export interface EvaluationRunGradingSummary {
  expected_assertions: number
  decided_assertions: number
  incomplete: boolean
}

export interface AgentEvaluationRunSummary {
  id: number
  agent_evaluation_id: number | null
  agent_evaluation_name: string | null
  tags: string[]
  agent_session_id: number | null
  chat_id: number | null
  status: EvaluationRunStatus
  passed_count: number
  total_count: number
  grading_complete: boolean
  grading_summary: EvaluationRunGradingSummary | null
  execution_time_ms: number | null
  total_turns: number | null
  git_commit_sha: string | null
  total_cost: number | null
  target_working_time_ms: number | null
  agent_type: string | null
  time_target_result: TimeTargetResult | null
  created_at: Timestamp
}

export interface TestDefinitionSnapshot {
  name: string
  agent_type: string
  initial_prompt: string
  max_iterations: number
  driver_config: EvaluationDriverConfig
  assertions: EvaluationAssertions
  preprocessed_at?: Timestamp
  preprocess_instruction?: string
  follow_up_instruction?: string
}

export interface ChatEvalSummary {
  id: number
  vertical: string | null
  organization: {
    orgid: string
    name: string
  } | null
  tool_call_count: number
  recent_activity: Array<
    | {
        type: "message"
        role: "customer" | "bot"
        text: string
      }
    | {
        type: "tool_call"
        name: string
      }
  >
  created_at: Timestamp
}

export interface AgentEvaluationRunDetail {
  id: number
  agent_evaluation_id: number | null
  agent_session_id: number | null
  chat_id: number | null
  driver_session_id: number | null
  grader_session_id: number | null
  sub_agent_session_ids: number[]
  chat_summary: ChatEvalSummary | null
  chat_agent_session_ids: number[]
  status: EvaluationRunStatus
  score: number | null
  passing_grade_threshold_used: number | null
  passed_count: number
  total_count: number
  grading_complete: boolean
  grading_summary: EvaluationRunGradingSummary | null
  execution_time_ms: number | null
  total_turns: number | null
  test_definition_snapshot: TestDefinitionSnapshot
  assertion_results: AssertionResult[]
  driver_actions: DriverAction[]
  error_message: string | null
  git_commit_sha: string | null
  cost_breakdown: EvaluationCostBreakdown | null
  time_target_result: TimeTargetResult | null
  created_at: Timestamp
  updated_at: Timestamp
}

export type AgentEvaluationReportStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"

export type AgentEvaluationReportType = "daily_core" | "weekly_core"

export interface AgentEvaluationReportSummary {
  id: number
  report_day: DayObject
  status: AgentEvaluationReportStatus
  report_type: AgentEvaluationReportType
}

export interface AgentEvaluationReportMetricTile {
  key: string
  label: string
  value: number | string | null
  previous_value?: number | string | null
  delta?: number | null
  unit?: string | null
}

export interface AgentEvaluationReportRun {
  id: number
  agent_evaluation_id: number | null
  agent_evaluation_name: string | null
  tags: string[]
  agent_session_id: number | null
  chat_id: number | null
  transcript_available: boolean
  status: EvaluationRunStatus
  score: number | null
  previous_status: EvaluationRunStatus | null
  previous_score: number | null
  score_delta: number | null
  regression: boolean
  improvement: boolean
  passed_count: number
  total_count: number
  time_target_result: TimeTargetResult | null
  execution_time_ms: number | null
  total_turns: number | null
  git_commit_sha: string | null
  agent_type: string | null
  total_cost: number | null
  total_tokens: number | null
  total_working_time_ms: number | null
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AgentEvaluationReportDailyLlmAnalysis {
  verdict: string
  attention_items: AgentEvaluationReportAttentionItem[]
}

export interface AgentEvaluationReportWeeklyLlmAnalysis {
  verdict: string
  weekly_read: string
  attention_items: AgentEvaluationReportAttentionItem[]
}

export type AgentEvaluationReportLlmAnalysis =
  | AgentEvaluationReportDailyLlmAnalysis
  | AgentEvaluationReportWeeklyLlmAnalysis

export interface AgentEvaluationReportAttentionItem {
  title: string
  severity: "high" | "medium"
  reason: string
  evidence_run_ids: number[]
  suggested_action: string
}

export interface AgentEvaluationReportScoreTrendPoint {
  report_day: DayObject
  average_score: number | null
  pass_rate: number | null
}

export interface AgentEvaluationReportStatusPoint {
  status: EvaluationRunStatus
  count: number
}

export interface AgentEvaluationReportScorePoint {
  name: string | null
  score: number | null
  previous_score: number | null
}

export interface AgentEvaluationReportCostPoint {
  name: string | null
  total_cost: number
}

export interface AgentEvaluationReportRuntimePoint {
  name: string | null
  execution_time_ms: number
}

export interface AgentEvaluationReportWeeklyPoint {
  report_day: DayObject
  pass_rate: number | null
}

export interface AgentEvaluationReportWeeklyCharts {
  pass_rate_series: AgentEvaluationReportWeeklyPoint[]
  cohort_pass_rate_series: {
    agent: AgentEvaluationReportWeeklyPoint[]
    chat: AgentEvaluationReportWeeklyPoint[]
  }
}

export interface AgentEvaluationReportNamedValue {
  agent_evaluation_id?: number
  agent_evaluation_name?: string | null
  name?: string | null
  domain?: string
  area?: string
  cohort?: "agent" | "chat"
  surface?: string
  value?: number
  statuses?: EvaluationRunStatus[]
  passed_count?: number
  failed_count?: number
}

export interface AgentEvaluationReportCompletedDailyDomainPassRate {
  domain: string
  passed: number
  total: number
  pass_rate: number | null
}

export interface AgentEvaluationReportCompletedDailyDomainCohortCell {
  domain: string
  cohort: "agent" | "chat"
  passed: number
  failed: number
  total: number
  pass_rate: number | null
}

export interface AgentEvaluationReportCompletedDailySummary {
  report_id: number
  report_day: DayObject
  total_runs: number
  failed_count: number
  pass_rate: number | null
  average_score: number | null
  severe_time_failure_count: number
  time_target_exception_count: number
  domain_pass_rates: AgentEvaluationReportCompletedDailyDomainPassRate[]
  domain_cohort_matrix: AgentEvaluationReportCompletedDailyDomainCohortCell[]
}

export interface AgentEvaluationReportCharts {
  score_trend: AgentEvaluationReportScoreTrendPoint[]
  status_distribution: AgentEvaluationReportStatusPoint[]
  score_by_evaluation: AgentEvaluationReportScorePoint[]
  cost_by_evaluation: AgentEvaluationReportCostPoint[]
  runtime_by_evaluation: AgentEvaluationReportRuntimePoint[]
  weekly?: AgentEvaluationReportWeeklyCharts
}

export interface AgentEvaluationReportWeeklyMetrics {
  seven_day_pass_rate: number | null
  thirty_day_pass_rate: number | null
  average_score: number | null
  error_timeout_count: number
  total_cost: number
  total_runtime_ms: number
  flaky_evaluations: AgentEvaluationReportNamedValue[]
  persistent_failures: AgentEvaluationReportNamedValue[]
  new_regressions: AgentEvaluationReportRun[]
  completed_daily_summaries: AgentEvaluationReportCompletedDailySummary[]
}

export interface AgentEvaluationReportDailyComparison {
  current: AgentEvaluationReportCompletedDailySummary
  previous: AgentEvaluationReportCompletedDailySummary | null
}

export interface AgentEvaluationReportMetricSlice {
  selected_count: number
  total_runs: number
  status_counts: Record<EvaluationRunStatus, number>
  terminal_count: number
  pass_rate: number | null
  average_score: number | null
  average_execution_time_ms: number | null
  total_cost: number
  total_tokens: number
  total_working_time_ms: number
  regressions: AgentEvaluationReportRun[]
  improvements: AgentEvaluationReportRun[]
  slowest_evaluations: AgentEvaluationReportRun[]
  costliest_evaluations: AgentEvaluationReportRun[]
}

export interface AgentEvaluationReportCohortMetrics {
  agent: AgentEvaluationReportMetricSlice
  chat: AgentEvaluationReportMetricSlice
}

export interface AgentEvaluationReportMetrics {
  selected_count: number
  total_runs: number
  status_counts: Record<EvaluationRunStatus, number>
  terminal_count: number
  pass_rate: number | null
  average_score: number | null
  average_execution_time_ms: number | null
  total_cost: number
  total_tokens: number
  total_working_time_ms: number
  agent_type_counts: Record<string, number>
  commit_sha_distribution: Record<string, number>
  regressions: AgentEvaluationReportRun[]
  improvements: AgentEvaluationReportRun[]
  slowest_evaluations: AgentEvaluationReportRun[]
  costliest_evaluations: AgentEvaluationReportRun[]
  cohorts: AgentEvaluationReportCohortMetrics
  weekly?: AgentEvaluationReportWeeklyMetrics
  daily_comparison?: AgentEvaluationReportDailyComparison
  daily_trend?: AgentEvaluationReportCompletedDailySummary[]
}

export interface AgentEvaluationReportDelivery {
  print_token: string
  slack_delivery_results: Record<string, unknown>[]
  slack_delivered_at?: string | null
  selected_agent_evaluation_ids: number[]
  source_daily_report_id?: number | null
}

export interface AgentEvaluationReportDetail {
  report: AgentEvaluationReportSummary
  metrics: AgentEvaluationReportMetrics
  llm_analysis: AgentEvaluationReportLlmAnalysis | null
  runs: AgentEvaluationReportRun[]
  charts: AgentEvaluationReportCharts
  delivery: AgentEvaluationReportDelivery
}

export type AgentEvaluationReportLlmAnalysisStatus = "complete" | "unavailable"

export interface AgentEvaluationReportListItem {
  id: number
  report_day: DayObject
  status: AgentEvaluationReportStatus
  report_type: AgentEvaluationReportType
  selected_count: number | null
  total_runs: number | null
  pass_rate: number | null
  slack_delivered_at: string | null
  llm_analysis_status: AgentEvaluationReportLlmAnalysisStatus
}

export interface AgentEvaluationReportsListResponse {
  reports: AgentEvaluationReportListItem[]
}

export interface AgentEvaluationReportRerunResponse {
  run_id: number
}

export type AgentBatchReadRequest =
  | { type: "evaluation_runs_show"; params: { id: number } }
  | { type: "session_events"; params: { id: number; after_id?: number } }
  | { type: "session_metrics"; params: { id: number } }

export interface AgentBatchReadPayload {
  requests: AgentBatchReadRequest[]
}

export type AgentBatchReadResult =
  | { index: number; ok: true; evaluation_run: AgentEvaluationRunDetail }
  | { index: number; ok: true; session_events: AgentSessionEventsResponse }
  | { index: number; ok: true; session_metrics: EnhancedAgentSessionMetrics }
  | { index: number; ok: false; error: string }

export interface AgentBatchReadResponse {
  results: AgentBatchReadResult[]
}

export interface GetEvaluationsResponse {
  evaluations: AgentEvaluationSummary[]
  paging_meta: PagingMeta
  default_model_id: string
}

export interface AgentEvaluationPickerRow {
  id: number
  name: string
  agent_type: string
  tags: string[]
}

export interface AgentEvaluationPickerFilter {
  q: string
  tags: string[]
  agent_type: string | null
}

export interface GetAgentEvaluationPickerResponse {
  evaluations: AgentEvaluationPickerRow[]
  paging_meta: PagingMeta
  matching_agent_types: string[]
}

export type RunEvaluationsBatchSelection =
  | { mode: "explicit"; evaluation_ids: number[] }
  | {
      mode: "all_matching"
      filter: AgentEvaluationPickerFilter
      excluded_evaluation_ids: number[]
      additional_evaluation_ids: number[]
    }

export interface GetEvaluationRunsResponse {
  runs: AgentEvaluationRunSummary[]
  paging_meta: PagingMeta
}

export interface RunEvaluationResponse {
  evaluation_id: number
  run_id: number
  status: string
}

export interface ChatTranscriptToolCall {
  tool_name: string
  args: Record<string, unknown>
  result_content?: unknown
  is_error: boolean
}

export interface ChatTranscriptMessage {
  id: number
  type: "customer" | "bot"
  sub_type: string | null
  body: string
  created_at: Timestamp
  tool_calls: ChatTranscriptToolCall[]
}

export interface ChatTranscriptTripEvent {
  id: number
  title: string | null
  type: string
  status: string
  start_at: Timestamp
  end_at: Timestamp
  all_day: boolean
  place_name: string | null
  end_place_name: string | null
}

export interface ChatTranscriptTrip {
  destination_name: string | null
  time_frame: TimeFrame
  itinerary_events: ChatTranscriptTripEvent[]
}

export interface ChatTranscriptAgentSession {
  id: number
  agent_type: string
  status: AgentSessionStatus
  title: string
}

export interface ChatTranscriptResponse {
  chat: {
    id: number
    name: string | null
    created_at: Timestamp
    vertical: string | null
    organization_id: number | null
  }
  messages: ChatTranscriptMessage[]
  trip: ChatTranscriptTrip | null
  agent_sessions: ChatTranscriptAgentSession[]
}

export interface GenerateEvalFromSessionResponse {
  name: string
  description: string
  generation_explanation: string
  agent_type: string
  initial_prompt: string
  assertions: {
    items: Array<{ assertion: string; severity: AssertionSeverity }>
  }
  driver_config: {
    driver_objective?: string
  }
  preprocess_instruction?: string
  follow_up_instruction?: string
  tags?: string[]
}

export interface BuildEvalFromDescriptionResponse {
  name: string
  description: string
  generation_explanation: string
  agent_type: string
  initial_prompt: string
  assertions: {
    items: Array<{ assertion: string; severity: AssertionSeverity }>
  }
  driver_config: {
    driver_objective?: string
  }
  preprocess_instruction?: string
  follow_up_instruction?: string
  tags?: string[]
}

export interface CreateEvaluationPayload {
  name: string
  description?: string | null
  agent_type?: string
  initial_prompt: string
  max_iterations?: number
  passing_grade_threshold?: number
  driver_config?: EvaluationDriverConfig
  assertions: EvaluationAssertions
  tags?: string[] | null
  preprocess_instruction?: string | null
  follow_up_instruction?: string | null
  session_user_id?: number | null
}

export interface UpdateEvaluationPayload {
  name?: string
  description?: string | null
  agent_type?: string
  initial_prompt?: string
  max_iterations?: number
  passing_grade_threshold?: number
  driver_config?: EvaluationDriverConfig
  assertions?: EvaluationAssertions
  tags?: string[] | null
  preprocess_instruction?: string | null
  follow_up_instruction?: string | null
  session_user_id?: number | null
}
